import React from 'react';
import { Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';

import merriweatherRegular from './Merriweather-Regular.ttf';
import merriweatherBold from './Merriweather-Bold.ttf';

import { formatCurrency } from '../../../billing/timesheets/BillingTools';
import logo from '../../../../app/coop-logo.png';
import dayjs from 'dayjs';

Font.register({
    family: 'Merriweather',
    fonts: [
        { src: merriweatherRegular },
        { src: merriweatherBold },
    ],
})

const css = StyleSheet.create({
    page: {
        color: '#212529',
        padding: '16pt',
        fontFamily: 'Merriweather',
    },
    logo: {
        fontSize: '16pt',
        fontWeight: 'bold',
        color: '#339989',
        marginBottom: '8px',
    },
    subtitle: {
        fontSize: '14pt',
        fontWeight: 'bold',
        color: '#212529',
        marginBottom: '8px',
    },
    header: {
        fontSize: '10pt'
    },
    category: {
        fontWeight: 'bold',
        marginBottom: '4px',
        marginTop: '8px',
        fontSize: '11pt',
    },
    description: {
        display: 'flex',
        justifyContent: 'flex-start',
    },
    row: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        fontSize: '10pt'
    },
    cell: {
        flex: 1,
        minWidth: '0px',
    },
    s1: {
        flex: 1
    },
    s2: {
        flex: 2
    },
    s3: {
        flex: 3
    },
    s4: {
        flex: 4
    },
    s5: {
        flex: 5
    },
    s6: {
        flex: 6
    },
    s7: {
        flex: 7
    },
    s8: {
        flex: 8
    },
    right: {
        display: 'flex',
        justifyContent: 'flex-end',
        textAlign: 'right',
    },
    left: {
        display: 'flex',
        justifyContent: 'flex-start'
    }
})

const gridBorderStyle = '0.5px solid lightgrey';

const gridItemCaption = {
    backgroundColor: '#DAD6D6',
    color: 'black',
    padding: '4px'
};

const underlineStyle = '0.5px solid lightgrey';

const SignatureRow = () => {
    return (
        <View style={{ ...css.row }}>
            <View style={{ minWidth: '300px', marginRight: '8px', borderBottom: underlineStyle }}>
                <Text style={{ ...css.category }}>Signature</Text>
            </View>
            <View style={{ minWidth: '200px', borderBottom: underlineStyle }}>
                <Text style={{ ...css.category }}>Date</Text>
            </View>
        </View>
    );
}

const FormRow = ({ lastRow = false, caption, value }) => {
    return (
        <View style={{ ...css.row, borderTop: gridBorderStyle, borderBottom: lastRow ? gridBorderStyle : '' }}>
            <View style={{ flex: 4 }}>
                <Text style={{ ...gridItemCaption }}>{caption}</Text>
            </View>
            <View style={{ flex: 8 }}>
                <Text style={{ padding: '4px', borderRight: gridBorderStyle }}>{value}</Text>
            </View>
        </View>
    );
}

const CategoryHeader = ({ title, css, numberStyleSmall, numberStyleLarge }) => {
    return (
        <React.Fragment>
            <Text style={css.category}>{title}</Text>
            <View style={{ ...css.row, backgroundColor: '#DAD6D6', fontWeight: 'semibold' }}>
                <Text style={{ ...css.cell, ...css.s6 }}>Description</Text>
                <Text style={{ ...css.cell, ...css.s1 }}>Date</Text>
                <Text style={numberStyleSmall}>Quantity</Text>
                <Text style={numberStyleSmall}>Rate</Text>
                <Text style={numberStyleLarge}>Net Amount</Text>
            </View>
        </React.Fragment>
    );
}

const BookingFormPDF = ({ digest }) => {
    const { intermediary, referral, request, courtCase, quote, notes, court } = digest;

    const numberStyleLarge = { ...css.cell, ...css.s3, ...css.right };
    const numberStyleSmall = { ...css.cell, ...css.s3, ...css.right };
    const netTotal = quote.netTotal();
    const grandTotal = quote.grandTotal();
    const vat = grandTotal - netTotal;

    const needs = referral.vulnerabilities?.length > 0 ?
        referral.vulnerabilities.map(v => v.description)
        : [notes?.clientNeeds?.text || 'Unspecified'];

    return <Document>
        <Page size="A4" style={css.page}>
            <View style={css.row}>
                <View style={{ flex: 6 }}>
                    <Text style={{ ...css.logo }}>The Intermediary Cooperative</Text>
                    <Text style={{ ...css.subtitle }}>Booking Form</Text>
                    <Text>Prepared for {referral.referrer?.name || '?'} at {request.organization}</Text>
                    <Text>Our Reference {referral.bookingReference}</Text>
                </View>
                <Image src={logo} style={{ flex: 6, justifyContent: 'flex-end', maxWidth: '100px', height: 'auto' }} />
            </View>

            <View>
                <Text style={{ ...css.category }}>Case Details</Text>
                <FormRow caption='Court' value={court?.name} />
                <FormRow caption='Case Number' value={courtCase.courtCaseNumber} />
            </View>

            <View>
                <Text style={{ ...css.category }}>Vulnerable Person</Text>
                <FormRow caption='Name' value={courtCase.serviceUserName} />
                <FormRow caption='Vulnerabilities' value={needs} lastRow={true} />
            </View>

            <View>
                <Text style={{ ...css.category }}>Intermediary Details</Text>
                <FormRow caption='Name' value={intermediary.profile?.title} />
                <FormRow caption='Email Address' value={intermediary.profile?.email} />
                <FormRow caption='Contact Number' value={intermediary.profile?.phoneNumber} lastRow={true} />
            </View>

            <CategoryHeader title="Intermediary Services" css={css} numberStyleLarge={numberStyleLarge} numberStyleSmall={numberStyleSmall} />
            {
                quote.items().filter(item => item.quantity > 0 && item.category === 'services').map(item => {
                    return <View style={css.row} key={item.key}>
                        <Text style={{ ...css.cell, ...css.s6 }}>{item.description}</Text>
                        <Text style={{ ...css.cell, ...css.s1 }}>{item.date || '-'}</Text>
                        <Text style={numberStyleSmall}>{item.quantity} {item.measure}s</Text>
                        <Text style={numberStyleSmall}>{formatCurrency(item.rate)}</Text>
                        <Text style={numberStyleLarge}>{formatCurrency(item.amount)}</Text>
                    </View>;
                })
            }
            <View style={{ ...css.row, fontWeight: 'bold' }}>
                <Text style={{ ...css.cell, ...css.s8, marginTop: '6px' }}>Total Intermediary Services</Text>
                <Text style={{ ...numberStyleLarge, marginTop: '6px' }}>{formatCurrency(quote.netCategoryTotal('services'))}</Text>
            </View>
            <View style={{ ...css.row, fontWeight: 'bold' }}>
                <Text style={{ ...css.cell, ...css.s8, marginTop: '6px' }}>Travel, Accommodation & Subsistence Expenses</Text>
                <Text style={{ ...numberStyleLarge, marginTop: '6px' }}>{formatCurrency(quote.netCategoryTotal('expenses'))}</Text>
            </View>
            <View style={{ ...css.row, fontWeight: 'bold' }}>
                <Text style={{ ...css.cell, ...css.s8, marginTop: '10px' }}>Net Booking Total</Text>
                <Text style={{ ...numberStyleLarge, marginTop: '10px' }}>{formatCurrency(netTotal)}</Text>
            </View>
            <View style={{ ...css.row, fontWeight: 'bold' }}>
                <Text style={{ ...css.cell, ...css.s8, marginTop: '2px' }}>VAT @ 20%</Text>
                <Text style={{ ...numberStyleLarge, marginTop: '2px' }}>{formatCurrency(vat)}</Text>
            </View>
            <View style={{ ...css.row, fontWeight: 'ultrabold' }}>
                <Text style={{ ...css.cell, ...css.s8, marginTop: '2px' }}>Gross Booking Total</Text>
                <Text style={{ ...numberStyleLarge, marginTop: '2px' }}>{formatCurrency(grandTotal)}</Text>
            </View>
            {
                // the value space is needed to make sure the cell border draws properly 
            }

            <View style={{ marginTop: '16px' }}>
                <Text style={{ ...css.category }}>Please complete all details and return by {dayjs().add(7, 'days').format('dddd DD MMMM YYYY')} to confirm your booking</Text>
                <FormRow caption='Email Address' value=' ' />
                <FormRow caption='Contact Phone Number' value=' ' />
                <FormRow caption='Invoice Contact Name' value=' ' />
                <FormRow caption='Invoice Address' value=' ' />
                <FormRow caption='Purchase Order Number' value=' ' lastRow={true} />
            </View>
            <View style={{ marginTop: '16px' }}>
                <SignatureRow />
            </View>

            <View style={{ ...css.row }}>
                <View style={{ minWidth: '300px', borderBottom: underlineStyle }}>
                    <Text style={{ ...css.category }}>Name</Text>
                </View>
            </View>
            <View style={{ ...css.row }}>
                <View style={{ minWidth: '300px', borderBottom: underlineStyle }}>
                    <Text style={{ ...css.category }}>Designation / Role</Text>
                </View>
            </View>

        </Page>
    </Document>
};

export default BookingFormPDF;